import React, { useState, useEffect, useCallback } from 'react';
import initSqlJs from 'sql.js';
import { authHeaders } from '../authToken';

const API_URL = process.env.REACT_APP_API_URL || '';

// Load the SQLite-WASM engine once and share the promise across runs. The wasm
// binary is served from /public, so locateFile points sql.js at /sql-wasm.wasm.
let _sqlPromise = null;
function getSql() {
  if (!_sqlPromise) {
    // Don't cache a rejection — if the wasm load fails (e.g. transient), reset
    // so the next Run can retry rather than being stuck on a dead promise.
    _sqlPromise = initSqlJs({ locateFile: (f) => `/${f}` }).catch((e) => {
      _sqlPromise = null;
      throw e;
    });
  }
  return _sqlPromise;
}

// ── Row comparison — mirrors the documented `check` schema ──────────────────
// Two cells are equal when, under an optional numeric tolerance, their numbers
// are within tolerance; otherwise a strict value/string compare (null-safe).
function cellsEqual(a, b, tol) {
  if (a === null || a === undefined || b === null || b === undefined) {
    return (a ?? null) === (b ?? null);
  }
  if (tol != null && tol > 0) {
    const na = Number(a), nb = Number(b);
    if (Number.isFinite(na) && Number.isFinite(nb)) return Math.abs(na - nb) <= tol;
  }
  if (typeof a === 'number' && typeof b === 'number') return a === b;
  return String(a) === String(b);
}

// Canonical sort key for set (unordered) comparison; tolerance buckets numbers
// so near-equal aggregate rows sort together.
function rowKey(row, tol) {
  return row
    .map((v) => {
      if (v === null || v === undefined) return '∅';
      if (tol != null && tol > 0 && typeof v === 'number') return String(Math.round(v / tol));
      return String(v);
    })
    .join('');
}

// Compare an actual result grid { columns, rows } against the exercise `check`.
// Returns { pass, reason }.
function checkResult(actual, check) {
  const expCols = check.columns || [];
  const expRows = check.rows || [];
  const tol = check.numeric_tolerance;

  // Column names must match expected order (case-insensitive); skip when both
  // sides are empty (a zero-row SELECT can't report column names via exec).
  if (!(actual.rows.length === 0 && expRows.length === 0)) {
    const lc = (xs) => xs.map((c) => String(c).toLowerCase());
    const a = lc(actual.columns), e = lc(expCols);
    if (a.length !== e.length || a.some((c, i) => c !== e[i])) {
      return { pass: false, reason: `Expected columns: ${expCols.join(', ')}` };
    }
  }

  if (actual.rows.length !== expRows.length) {
    return { pass: false, reason: `Expected ${expRows.length} row(s), got ${actual.rows.length}.` };
  }

  let aRows = actual.rows, eRows = expRows;
  if (!check.ordered) {
    aRows = [...aRows].sort((x, y) => rowKey(x, tol).localeCompare(rowKey(y, tol)));
    eRows = [...eRows].sort((x, y) => rowKey(x, tol).localeCompare(rowKey(y, tol)));
  }
  for (let r = 0; r < eRows.length; r++) {
    for (let c = 0; c < eRows[r].length; c++) {
      if (!cellsEqual(aRows[r][c], eRows[r][c], tol)) {
        return { pass: false, reason: 'Rows do not match the expected result.' };
      }
    }
  }
  return { pass: true, reason: 'Correct!' };
}

// ── Schema reference, parsed from seed_sql ──────────────────────────────────
// The exercise ships its whole database as `seed_sql` (CREATE TABLE … + INSERT).
// We pull the table + column names straight out of those CREATE TABLE blocks so
// students can see the exact identifiers to use instead of guessing. Deriving
// from the seed means the panel always matches the data the query runs against,
// with no second source of truth to keep in sync.
//
// Returns [{ table, columns: [{ name, pk, fk }] }]. Lines that are table-level
// constraints (PRIMARY KEY (...), FOREIGN KEY …, UNIQUE …, CHECK …, CONSTRAINT)
// carry no new column name and are skipped — but an inline "REFERENCES" on a
// column flags it as a foreign key, and an inline "PRIMARY KEY" flags the PK.
const CONSTRAINT_RE = /^(PRIMARY|FOREIGN|UNIQUE|CHECK|CONSTRAINT)\b/i;

function parseSchema(seedSql) {
  if (!seedSql) return [];
  const tables = [];
  const re = /CREATE\s+TABLE\s+(\w+)\s*\(/gi;
  let m;
  while ((m = re.exec(seedSql))) {
    const name = m[1];
    // Walk from the opening paren to its matching close to grab the body.
    let depth = 0, i = m.index + m[0].length - 1, body = '';
    for (; i < seedSql.length; i++) {
      const ch = seedSql[i];
      if (ch === '(') depth++;
      else if (ch === ')') { depth--; if (depth === 0) break; }
      if (depth >= 1 && i > m.index + m[0].length - 1) body += ch;
    }
    // Split the body on top-level commas (PK lists like "(a, b)" stay intact).
    const parts = [];
    let buf = '', d = 0;
    for (const ch of body) {
      if (ch === '(') d++;
      else if (ch === ')') d--;
      if (ch === ',' && d === 0) { parts.push(buf); buf = ''; }
      else buf += ch;
    }
    if (buf.trim()) parts.push(buf);

    const columns = [];
    for (const raw of parts) {
      const part = raw.trim();
      if (!part || CONSTRAINT_RE.test(part)) continue;
      const colName = part.split(/\s+/)[0];
      columns.push({
        name: colName,
        pk: /\bPRIMARY\s+KEY\b/i.test(part),
        fk: /\bREFERENCES\b/i.test(part),
      });
    }
    tables.push({ table: name, columns });
  }
  return tables;
}

// Run student SQL against a FRESH seeded DB and return the grid to check.
// select   → run the student's query, grab its result set.
// mutation → run the student's statements, then run verify_sql and grab THAT.
function runExercise(SQL, exercise, studentSql) {
  const db = new SQL.Database();
  try {
    db.run(exercise.seed_sql);
    const check = exercise.check;
    const res = check.mode === 'mutation'
      ? (db.run(studentSql), db.exec(check.verify_sql))
      : db.exec(studentSql);
    const last = res.length ? res[res.length - 1] : { columns: [], values: [] };
    return { columns: last.columns, rows: last.values };
  } finally {
    db.close();
  }
}

/**
 * Theme A SQL exercises for one subtopic scope (A3.3).
 *
 * Every exercise runs CLIENT-SIDE in sql.js (SQLite compiled to WebAssembly):
 * a fresh in-memory database is built from `seed_sql` before each Run, the
 * student's SQL executes in the browser's WASM sandbox, and the result is
 * checked locally against the `check` block shipped by the API. Nothing
 * executes on the server — a malicious statement only touches the student's
 * own throwaway in-tab database. A pass is banked via POST /quiz-attempt,
 * where the statement_id and XP weighting are derived server-side.
 *
 * Props:
 *   scopes (string[]) — one or more scopes to pull SQL exercises from; mirrors
 *     Quiz/Flashcards. `scope` (string) is accepted for the legacy ThemeAHub
 *     path. Scopes without SQL exercises (anything outside A3.3) contribute
 *     nothing, so a mixed selection naturally filters down to the SQL ones.
 *   level, scopeTitle, onBack — as in Quiz / Flashcards.
 */
export default function SqlExercise({ scopes, scope, level, scopeTitle, onBack }) {
  const [exercises, setExercises] = useState([]);
  const [idx,       setIdx]       = useState(0);
  const [loading,   setLoading]   = useState(true);

  const scopeKey = (scopes && scopes.length ? scopes : (scope ? [scope] : []))
    .slice().sort().join(',');

  // Per-exercise editor + run state
  const [sqlText, setSqlText] = useState('');
  const [running, setRunning] = useState(false);
  const [error,   setError]   = useState(null);   // execution error string
  const [grid,    setGrid]    = useState(null);    // { columns, rows } from last run
  const [result,  setResult]  = useState(null);    // { pass, awarded_xp } once checked

  // Load SQL exercises across the selected scopes (answers stripped, check
  // block kept), merged and de-duplicated by id.
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const results = await Promise.all(
          scopeKey.split(',').filter(Boolean).map(s =>
            fetch(`${API_URL}/api/theme-a/content/${s}?level=${level}`, { headers: authHeaders() })
              .then(r => r.json())
          )
        );
        if (!cancelled) {
          const seen = new Set();
          const merged = [];
          for (const d of results) {
            for (const ex of (d.sql_exercises || [])) {
              if (!seen.has(ex.id)) { seen.add(ex.id); merged.push(ex); }
            }
          }
          setExercises(merged);
          setIdx(0);
        }
      } catch (_) {
        if (!cancelled) setExercises([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [scopeKey, level]);

  const current = exercises[idx];
  const schema = React.useMemo(
    () => (current ? parseSchema(current.seed_sql) : []),
    [current]
  );

  // Reset the editor to the current exercise's starter SQL whenever it changes.
  useEffect(() => {
    setSqlText(current ? (current.starter_sql || '') : '');
    setError(null);
    setGrid(null);
    setResult(null);
  }, [current]);

  const runSql = useCallback(async () => {
    if (running || !current) return;
    setRunning(true);
    setError(null);
    setResult(null);
    try {
      const SQL = await getSql();
      const grid = runExercise(SQL, current, sqlText);   // throws on SQL errors
      setGrid(grid);
      const verdict = checkResult(grid, current.check);
      // On a pass, bank the attempt. statement_id + XP weighting are derived
      // server-side from the item id; the client only asserts correct:true.
      if (verdict.pass) {
        try {
          const r = await fetch(`${API_URL}/api/theme-a/quiz-attempt`, {
            method:  'POST',
            headers: authHeaders({ 'Content-Type': 'application/json' }),
            body:    JSON.stringify({ question_id: current.id, correct: true }),
          });
          const d = await r.json();
          verdict.awarded_xp = d.awarded_xp || 0;
        } catch (_) { /* XP banking is best-effort; the pass still stands */ }
      }
      setResult(verdict);
    } catch (e) {
      setGrid(null);
      setError(String(e && e.message ? e.message : e));
    } finally {
      setRunning(false);
    }
  }, [running, current, sqlText]);

  const goTo = useCallback((i) => setIdx(i), []);

  return (
    <div className="ta-canvas">
      <div className="ta-header">
        <button className="ta-back" onClick={onBack}>‹ Topics</button>
        <div>
          <h1 className="ta-title">SQL</h1>
          <p className="ta-subtitle">{scopeTitle} · {level.toUpperCase()}</p>
        </div>
      </div>

      {loading ? (
        <div className="ta-empty">Loading…</div>
      ) : exercises.length === 0 ? (
        <div className="ta-empty">No SQL exercises for this selection.</div>
      ) : (
        <div className="ta-study">
          <div className="ta-progress">
            Exercise {idx + 1} / {exercises.length}
            {current.hl_only && <span className="ta-hl-badge" style={{ marginLeft: 8 }}>HL</span>}
          </div>

          <div className="ta-quiz-card">
            <p className="ta-question">{current.prompt}</p>

            {schema.length > 0 && (
              <details className="ta-sql-schema">
                <summary>Tables &amp; fields</summary>
                <div className="ta-sql-schema-tables">
                  {schema.map((t) => (
                    <div key={t.table} className="ta-sql-schema-table">
                      <code className="ta-sql-schema-name">{t.table}</code>
                      <div className="ta-sql-schema-cols">
                        {t.columns.map((col) => (
                          <span key={col.name} className="ta-sql-schema-col">
                            {col.name}
                            {col.pk && <span className="ta-sql-key" title="Primary key">PK</span>}
                            {col.fk && <span className="ta-sql-key ta-sql-key-fk" title="Foreign key">FK</span>}
                          </span>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </details>
            )}

            <textarea
              className="ta-sql-editor"
              value={sqlText}
              onChange={(e) => setSqlText(e.target.value)}
              spellCheck={false}
              rows={6}
            />

            <div className="ta-controls" style={{ marginTop: 12 }}>
              <button className="ta-btn ta-btn-primary" onClick={runSql} disabled={running}>
                {running ? 'Running…' : 'Run'}
              </button>
            </div>

            {error && <div className="ta-feedback"><span className="ta-verdict ta-verdict-wrong">{error}</span></div>}

            {result && !error && (
              <div className="ta-feedback">
                <span className={`ta-verdict ${result.pass ? 'ta-verdict-correct' : 'ta-verdict-wrong'}`}>
                  {result.pass ? 'Correct!' : result.reason}
                  {result.pass && result.awarded_xp > 0 && (
                    <span className="ta-xp" style={{ marginLeft: 8 }}>+{result.awarded_xp} XP</span>
                  )}
                </span>
              </div>
            )}

            {grid && (
              <div className="ta-sql-results">
                <table className="ta-sql-grid">
                  <thead>
                    <tr>{grid.columns.map((c, i) => <th key={i}>{c}</th>)}</tr>
                  </thead>
                  <tbody>
                    {grid.rows.map((row, r) => (
                      <tr key={r}>{row.map((v, c) => <td key={c}>{String(v)}</td>)}</tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          <div className="ta-controls" style={{ marginTop: 16 }}>
            <button
              className="ta-btn ta-btn-secondary"
              onClick={() => goTo(idx - 1)}
              disabled={idx === 0}
            >
              ‹ Previous
            </button>
            <button
              className="ta-btn ta-btn-secondary"
              onClick={() => goTo(idx + 1)}
              disabled={idx >= exercises.length - 1}
            >
              Next ›
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
